import * as THREE from "three";

/**
 * Blazor JS-interop bridge for the "Voice Line" symbol, rendered with Three.js.
 *
 * A glowing horizontal line that erupts into rainbow wave pulses (like a voice waveform),
 * with chromatic dispersion and a soft reflection underneath. The whole picture is drawn by a
 * single full-screen fragment shader; Three.js owns the renderer, resize, pixel-ratio and
 * lifecycle, while a tiny JS simulation spawns / ages the pulses and feeds them as uniforms.
 *
 * Exposed on window.App as:
 *  - App.voiceLineInit(elementId, options)     mount a Three.js canvas into the container div
 *  - App.voiceLineUpdate(elementId, options)   update uniforms live (no re-mount)
 *  - App.voiceLineDispose(elementId)           stop the loop and free WebGL resources
 *
 * Called from VoiceLineSymbol.razor.cs.
 */

export interface VoiceLineInteropOptions {
    lineColor?: string;     // hex, core color of the base line glow
    bgColor?: string;       // hex
    speed?: number;         // simulation speed multiplier
    activity?: number;      // how often pulses are spawned
    amplitude?: number;     // pulse height multiplier
    glow?: number;          // overall glow intensity
    thickness?: number;     // line thickness multiplier
    dispersion?: number;    // chromatic aberration strength
    reflection?: number;    // reflection strength (0 = off)
    renderScale?: number;   // internal resolution multiplier
}

const MAX_PULSES = 3;

const VERTEX = /* glsl */ `
varying vec2 vUv;
void main() {
    vUv = uv;
    gl_Position = vec4(position.xy, 0.0, 1.0);
}`;

const FRAGMENT = /* glsl */ `
precision highp float;

#define LAYERS 12
#define MAX_PULSES ${MAX_PULSES}

varying vec2 vUv;

uniform vec2  uRes;
uniform float uTime;
uniform vec3  uLineColor;
uniform vec3  uBgColor;
uniform float uGlow;
uniform float uThick;
uniform float uDisp;
uniform float uRefl;
// x = center, y = amplitude (signed, already enveloped), z = width, w = phase
uniform vec4  uPulse[MAX_PULSES];

vec3 rainbow(float t) {
    // outer layers: blue -> cyan/green -> yellow/orange -> red/pink (inner)
    vec3 c0 = vec3(0.10, 0.28, 1.00);
    vec3 c1 = vec3(0.12, 0.95, 0.85);
    vec3 c2 = vec3(1.00, 0.85, 0.35);
    vec3 c3 = vec3(1.00, 0.30, 0.28);
    if (t < 0.34) return mix(c0, c1, t / 0.34);
    if (t < 0.67) return mix(c1, c2, (t - 0.34) / 0.33);
    return mix(c2, c3, (t - 0.67) / 0.33);
}

// Pulse shape: gaussian envelope * cosine. Returns value (x) and slope (y).
vec2 pulseShape(float d, float w, float phase) {
    float k   = 1.7 / w;
    float env = exp(-d * d / (2.0 * w * w));
    float a   = k * d - phase;
    float v   = env * cos(a);
    float dv  = env * (-k * sin(a) - (d / (w * w)) * cos(a));
    return vec2(v, dv);
}

// Sum of all pulses for one layer.  t in [0,1] selects the layer (0 outer, 1 inner).
vec2 waveAt(float x, float t) {
    vec2 r = vec2(0.0);
    for (int j = 0; j < MAX_PULSES; j++) {
        vec4 p = uPulse[j];
        float w = p.z;
        float amp = p.y * mix(1.0, 0.5, t);
        float shift = (t - 0.5) * 1.5 * w;       // fan the layers out horizontally
        vec2 s = pulseShape(x - p.x - shift, w, p.w + t * 0.55);
        r += amp * s;
    }
    return r;
}

vec3 hazeAt(vec2 p) {
    vec3 h = vec3(0.0);
    for (int j = 0; j < MAX_PULSES; j++) {
        vec4 q = uPulse[j];
        float a = abs(q.y);
        if (a < 0.002) continue;
        float s = sign(q.y);
        float w = q.z;
        // wide blue mass trailing the pulse
        vec2 d1 = vec2((p.x - (q.x - 0.9 * w)) / (1.5 * w), (p.y + s * 0.25 * a) / (0.9 * a + 0.01));
        h += vec3(0.05, 0.14, 0.95) * exp(-dot(d1, d1)) * 0.55;
        // warm core
        vec2 d2 = vec2((p.x - (q.x + 0.35 * w)) / (0.8 * w), (p.y - s * 0.45 * a) / (0.6 * a + 0.01));
        h += vec3(1.0, 0.28, 0.12) * exp(-dot(d2, d2)) * 0.42;
    }
    return h;
}

vec3 scene(vec2 p, float blur) {
    float line = 0.0016 * uThick * blur;
    vec3 col = vec3(0.0);

    // base horizontal line (rippled a little by the pulses)
    vec2 b = waveAt(p.x, 1.0);
    float bd = (p.y - b.x * 0.18 - 0.003 * sin(p.x * 7.0 + uTime * 0.8)) / sqrt(1.0 + b.y * b.y * 0.03);
    float bl = line * line / (bd * bd + line * line);
    float edge = 0.55 + 0.45 * smoothstep(1.0, 0.25, abs(p.x) / (0.5 * uRes.x / uRes.y));
    col += uLineColor * bl * 1.1 * edge;
    col += vec3(1.0) * exp(-bd * bd / (line * line * 0.8)) * 1.1 * edge;
    col += uLineColor * exp(-abs(bd) * 22.0 / blur) * 0.22 * edge;

    // rainbow layers
    for (int i = 0; i < LAYERS; i++) {
        float t = float(i) / float(LAYERS - 1);
        vec2 w = waveAt(p.x, t);
        float d = (p.y - w.x) / sqrt(1.0 + w.y * w.y);
        float lw = line * (1.3 + 0.8 * (1.0 - t));
        float g = lw * lw / (d * d + lw * lw);
        float act = clamp(abs(w.x) * 40.0, 0.0, 1.0); // only visible while the wave is up
        col += rainbow(t) * (g * 0.36 + exp(-d * d / (lw * lw * 16.0)) * 0.05) * act;
    }

    col += hazeAt(p) * 0.8;
    return col;
}

void main() {
    float aspect = uRes.x / uRes.y;
    vec2 p = (vUv - 0.5) * vec2(aspect, 1.0);

    // chromatic dispersion: shift the evaluation per channel
    float dsp = 0.0032 * uDisp;
    vec3 c;
    c.r = scene(p + vec2( dsp * 2.2,  dsp * 0.25), 1.0).r;
    c.g = scene(p, 1.0).g;
    c.b = scene(p - vec2( dsp * 2.2,  dsp * 0.25), 1.0).b;

    // reflection below the line: mirrored, compressed, blurred, fading with distance
    if (uRefl > 0.001 && p.y < 0.0) {
        vec2 m = vec2(p.x, -p.y * 1.7 - 0.012);
        vec3 r = scene(m, 5.0);
        float fade = exp(p.y * 7.0);
        c += r * fade * uRefl * 0.28;
    }

    c = uBgColor + c * uGlow;
    // soft filmic tone-map so overlaps bloom to white instead of clipping
    c = 1.0 - exp(-c * 1.35);
    gl_FragColor = vec4(c, 1.0);
}`;

interface Pulse {
    active: boolean;
    x: number;
    dir: number;
    age: number;
    dur: number;
    peak: number;   // signed peak amplitude (height units)
    width: number;
    phase: number;
}

interface VoiceLineMount {
    renderer: THREE.WebGLRenderer;
    scene: THREE.Scene;
    camera: THREE.OrthographicCamera;
    material: THREE.ShaderMaterial;
    geometry: THREE.PlaneGeometry;
    container: HTMLElement;
    observer: ResizeObserver;
    raf: number;
    options: VoiceLineInteropOptions;
    pulses: Pulse[];
    time: number;
    lastFrame: number;
    nextSpawn: number;
}

const mounts = new Map<string, VoiceLineMount>();

function hexToVec3(hex: string | undefined, fallback: [number, number, number]): THREE.Vector3 {
    if (!hex) return new THREE.Vector3(...fallback);
    const h = hex.replace("#", "");
    if (h.length !== 6) return new THREE.Vector3(...fallback);
    return new THREE.Vector3(
        parseInt(h.slice(0, 2), 16) / 255,
        parseInt(h.slice(2, 4), 16) / 255,
        parseInt(h.slice(4, 6), 16) / 255,
    );
}

function rand(min: number, max: number): number {
    return min + Math.random() * (max - min);
}

function applyOptions(m: VoiceLineMount): void {
    const o = m.options;
    const u = m.material.uniforms;
    u.uLineColor.value = hexToVec3(o.lineColor, [0.12, 0.35, 1.0]);
    u.uBgColor.value = hexToVec3(o.bgColor, [0.004, 0.008, 0.03]);
    u.uGlow.value = o.glow ?? 1;
    u.uThick.value = o.thickness ?? 1;
    u.uDisp.value = o.dispersion ?? 1;
    u.uRefl.value = o.reflection ?? 1;
    resize(m);
}

function resize(m: VoiceLineMount): void {
    const w = Math.max(1, m.container.clientWidth);
    const h = Math.max(1, m.container.clientHeight);
    const dpr = Math.min(window.devicePixelRatio || 1, 2) * (m.options.renderScale ?? 0.75);
    m.renderer.setPixelRatio(dpr);
    m.renderer.setSize(w, h, false);
    const canvas = m.renderer.domElement;
    canvas.style.width = "100%";
    canvas.style.height = "100%";
    m.material.uniforms.uRes.value.set(w, h);
}

function spawnPulse(m: VoiceLineMount): void {
    const slot = m.pulses.find(p => !p.active);
    if (!slot) return;
    const amp = m.options.amplitude ?? 1;
    slot.active = true;
    slot.age = 0;
    slot.dur = rand(1.1, 2.2);
    slot.x = rand(-0.55, 0.55);
    slot.dir = Math.random() < 0.5 ? -1 : 1;
    slot.peak = rand(0.07, 0.24) * amp * (Math.random() < 0.5 ? -1 : 1);
    slot.width = rand(0.04, 0.09);
    slot.phase = rand(-0.4, 0.8);
}

function step(m: VoiceLineMount, dt: number): void {
    const speed = m.options.speed ?? 1;
    const activity = m.options.activity ?? 1;
    const sdt = dt * speed;
    m.time += sdt;

    m.nextSpawn -= sdt * activity;
    if (m.nextSpawn <= 0) {
        spawnPulse(m);
        m.nextSpawn = rand(0.25, 1.1);
    }

    const uPulse = m.material.uniforms.uPulse.value as THREE.Vector4[];
    for (let i = 0; i < MAX_PULSES; i++) {
        const p = m.pulses[i];
        if (!p.active) {
            uPulse[i].set(0, 0, 0.1, 0);
            continue;
        }
        p.age += sdt;
        const u = p.age / p.dur;
        if (u >= 1) {
            p.active = false;
            uPulse[i].set(0, 0, 0.1, 0);
            continue;
        }
        // fast attack, slower release
        const env = u < 0.22
            ? Math.sin((u / 0.22) * Math.PI * 0.5)
            : Math.pow(Math.cos(((u - 0.22) / 0.78) * Math.PI * 0.5), 1.6);
        p.x += p.dir * sdt * 0.05;
        uPulse[i].set(p.x, p.peak * env, p.width * (0.85 + 0.3 * env), p.phase);
    }
    m.material.uniforms.uTime.value = m.time;
}

export function voiceLineInit(elementId: string, options: VoiceLineInteropOptions): void {
    voiceLineDispose(elementId);

    const container = document.getElementById(elementId);
    if (!container) return;

    try {
        const renderer = new THREE.WebGLRenderer({ antialias: false, alpha: false, powerPreference: "high-performance" });
        const scene = new THREE.Scene();
        const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
        const geometry = new THREE.PlaneGeometry(2, 2);
        const material = new THREE.ShaderMaterial({
            vertexShader: VERTEX,
            fragmentShader: FRAGMENT,
            depthTest: false,
            depthWrite: false,
            uniforms: {
                uRes: { value: new THREE.Vector2(1, 1) },
                uTime: { value: 0 },
                uLineColor: { value: new THREE.Vector3() },
                uBgColor: { value: new THREE.Vector3() },
                uGlow: { value: 1 },
                uThick: { value: 1 },
                uDisp: { value: 1 },
                uRefl: { value: 1 },
                uPulse: { value: Array.from({ length: MAX_PULSES }, () => new THREE.Vector4(0, 0, 0.1, 0)) },
            },
        });
        scene.add(new THREE.Mesh(geometry, material));

        container.appendChild(renderer.domElement);

        const mount: VoiceLineMount = {
            renderer, scene, camera, material, geometry, container,
            observer: undefined as unknown as ResizeObserver,
            raf: 0,
            options: { ...options },
            pulses: Array.from({ length: MAX_PULSES }, () => ({
                active: false, x: 0, dir: 1, age: 0, dur: 1, peak: 0, width: 0.08, phase: 0,
            })),
            time: 0,
            lastFrame: performance.now(),
            nextSpawn: 0,
        };

        mount.observer = new ResizeObserver(() => resize(mount));
        mount.observer.observe(container);
        applyOptions(mount);
        mounts.set(elementId, mount);

        const loop = (now: number): void => {
            mount.raf = requestAnimationFrame(loop);
            if (document.hidden) { mount.lastFrame = now; return; }
            const dt = Math.min(0.05, (now - mount.lastFrame) / 1000);
            mount.lastFrame = now;
            step(mount, dt);
            renderer.render(scene, camera);
        };
        mount.raf = requestAnimationFrame(loop);
    } catch (error) {
        console.error("[VoiceLine] Failed to initialize:", error);
    }
}

export function voiceLineUpdate(elementId: string, options: VoiceLineInteropOptions): void {
    const mount = mounts.get(elementId);
    if (!mount) return;
    try {
        mount.options = { ...options };
        applyOptions(mount);
    } catch (error) {
        console.error("[VoiceLine] Failed to update:", error);
    }
}

export function voiceLineDispose(elementId: string): void {
    const mount = mounts.get(elementId);
    if (!mount) return;
    try {
        cancelAnimationFrame(mount.raf);
        mount.observer.disconnect();
        mount.geometry.dispose();
        mount.material.dispose();
        mount.renderer.dispose();
        mount.renderer.forceContextLoss();
        mount.renderer.domElement.remove();
    } catch (error) {
        console.error("[VoiceLine] Failed to dispose:", error);
    }
    mounts.delete(elementId);
}

// app.ts (which creates window.App) may run before or after this module inside the same esbuild
// bundle depending on the order of the entry file's imports. Attach immediately when possible and
// always re-attach on the next tick, once the whole bundle has finished executing.
const attachToApp = (): void => {
    const w = window as any;
    const app = w.App ?? (w.App = {});
    app.voiceLineInit = voiceLineInit;
    app.voiceLineUpdate = voiceLineUpdate;
    app.voiceLineDispose = voiceLineDispose;
};

attachToApp();
setTimeout(attachToApp, 0);

export {};
